import {Alert, Card, DataStatus, Separator} from 'platform/components';
import {Show, VStack} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {
  AftersalesPaymentMethod,
  useDeleteCheckoutDepositPaymentMutation,
  useGetCheckoutByIdQuery,
  useGetCheckoutPaymentsQuery,
  usePostCheckoutDepositPaymentCreateMutation,
  usePutCheckoutDepositPaymentAllowMutation,
  usePutCheckoutDepositPaymentDisallowMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CheckoutPaymentForm} from './CheckoutPaymentForm';

interface DepositPaymentsCardProp extends RequiredTestIdProps {
  checkoutId: string;
  defaultPaymentType?: AftersalesPaymentMethod;
  isEditingDisabled?: boolean;
  isCompletingDisabled?: boolean;
}

export function DepositPaymentsCard(props: DepositPaymentsCardProp) {
  const {
    data: checkoutData,
    isLoading: isCheckoutLoading,
    isError: isCheckoutError,
  } = useGetCheckoutByIdQuery({checkoutId: props.checkoutId});

  const {
    data: paymentsData,
    isLoading: isPaymentsLoading,
    isError: isPaymentsError,
  } = useGetCheckoutPaymentsQuery({checkoutId: props.checkoutId});

  const [putCheckoutDepositPaymentAllow, {isLoading: isAllowingLoading}] =
    usePutCheckoutDepositPaymentAllowMutation();
  const [putCheckoutDepositPaymentDisallow, {isLoading: isDisallowingLoading}] =
    usePutCheckoutDepositPaymentDisallowMutation();
  const [postCheckoutDepositPaymentCreate] = usePostCheckoutDepositPaymentCreateMutation();
  const [deleteCheckoutDepositPayment] = useDeleteCheckoutDepositPaymentMutation();

  const handleSwitchDepositPayments = (shouldAllow: boolean) => {
    if (shouldAllow) {
      return putCheckoutDepositPaymentAllow({checkoutId: props.checkoutId})
        .unwrap()
        .catch(handleApiError);
    }

    return putCheckoutDepositPaymentDisallow({checkoutId: props.checkoutId})
      .unwrap()
      .catch(handleApiError);
  };

  const handleAddAnotherDeposit = () =>
    postCheckoutDepositPaymentCreate({checkoutId: props.checkoutId}).unwrap().catch(handleApiError);

  const handleRemoveDeposit = (paymentId: string) => {
    if (depositPayments?.length === 1) {
      return putCheckoutDepositPaymentDisallow({checkoutId: props.checkoutId})
        .unwrap()
        .catch(handleApiError);
    }

    return deleteCheckoutDepositPayment({checkoutId: props.checkoutId, paymentId})
      .unwrap()
      .catch(handleApiError);
  };

  const depositPayments = paymentsData?.filter((payment) => payment.discriminator === 'DEPOSIT');

  const balancePayment = paymentsData?.find((payment) => payment.discriminator === 'BALANCE');

  const isBalancePaymentIssued =
    balancePayment?.paymentState === 'PENDING' || balancePayment?.paymentState === 'PAID';

  const allDepositPaymentsPaid =
    !!depositPayments?.length &&
    depositPayments.every((payment) => payment.paymentState === 'PAID');
  const hasSomePaidPayment =
    !!depositPayments?.length && depositPayments.some((payment) => payment.paymentState === 'PAID');

  const isLoading = isPaymentsLoading || isCheckoutLoading;

  const isError = isPaymentsError || isCheckoutError;

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Card
        control={{
          type: 'switch',
          onChange: handleSwitchDepositPayments,
          value: !!checkoutData?.isDepositAllowed,
          isDisabled:
            isAllowingLoading ||
            isDisallowingLoading ||
            hasSomePaidPayment ||
            props.isEditingDisabled ||
            isBalancePaymentIssued,
        }}
        title={i18n.t('entity.checkout.deposit')}
        variant="inlineGrey"
        isExpanded={checkoutData?.isDepositAllowed ?? false}
        isClosedByDefault
      >
        <VStack spacing={4}>
          {depositPayments?.map((payment, index) => {
            const isLastPayment = depositPayments.length - 1 === index;

            return (
              <>
                <Show when={index > 0}>
                  <Separator spacing={0} />
                </Show>
                <Show when={allDepositPaymentsPaid}>
                  <Alert
                    title={i18n.t('entity.checkout.labels.allPaymentsPaid')}
                    variant="success"
                  />
                </Show>
                <CheckoutPaymentForm
                  key={payment.checkoutPaymentId}
                  paymentId={payment.checkoutPaymentId}
                  checkoutId={props.checkoutId}
                  isSubmitButtonDisabled={
                    isNilOrEmpty(checkoutData?.billingInformation?.customerInformation.id) ||
                    isNilOrEmpty(checkoutData?.billingInformation?.contractInformation?.id)
                  }
                  customerId={checkoutData?.billingInformation?.customerInformation.id ?? ''}
                  defaultPaymentType={props.defaultPaymentType}
                  isForeignCurrencyAllowed={checkoutData?.isForeignCurrencyPayment}
                  onAddAnotherDepositClick={
                    isLastPayment && !isBalancePaymentIssued ? handleAddAnotherDeposit : undefined
                  }
                  onRemoveDepositClick={() => handleRemoveDeposit(payment.checkoutPaymentId)}
                  isEditingDisabled={props.isEditingDisabled}
                  isCompletingDisabled={props.isCompletingDisabled}
                  data-testid={suffixTestId(`depositPayment-${index}`, props)}
                />
              </>
            );
          })}
        </VStack>
      </Card>
    </DataStatus>
  );
}
